/**
 * Admin: Refresh Event Sources with WebIQ
 *
 * Job 1 of the pipeline. Finds which websites list events for each entity.
 * Wraps `refreshAndProcessEventSources`, which returns a processId immediately
 * and is then polled for progress.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Globe, Play } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  startEventSourceRefresh,
  getWebIqDiscoveryStatus,
  saveLastRun,
  loadLastRun,
  type EventSourceRefreshParams,
  type WebIqDiscoveryStatus,
  type LastRunRecord,
} from '../services/adminApi';
import {
  Card,
  CardTitle,
  CardDescription,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  ConfirmDialog,
  Banner,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  Field,
  Label,
  Input,
  InlineLabel,
  ButtonRow,
  SampleTable,
} from '../components/admin/AdminUI';
import { displayEntityType } from '../utils/entityTerminology';

const ACTION = 'refreshEventSources';
const POLL_INTERVAL_MS = 5000;

const ENTITY_TYPES = ['Partner', 'Company', 'Organization', 'Industry', 'City', 'University'];

const TERMINAL_STATUSES = ['completed', 'completed_with_errors', 'failed'];

type Scope = 'types' | 'ids';

export const AdminRefreshEventSourcesPage: React.FC = () => {
  const { userInfo } = useAuth();

  const [scope, setScope] = useState<Scope>('types');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['Partner']);
  const [entityIds, setEntityIds] = useState<string>('');
  const [maxResults, setMaxResults] = useState<number>(10);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [processId, setProcessId] = useState<string | null>(null);
  const [status, setStatus] = useState<WebIqDiscoveryStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<LastRunRecord<WebIqDiscoveryStatus> | null>(null);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedRef = useRef(false);

  useEffect(() => {
    setLastRun(loadLastRun<WebIqDiscoveryStatus>(ACTION));
  }, []);

  const isRunning = Boolean(
    processId && status && !TERMINAL_STATUSES.includes(status.status),
  );

  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type],
    );
  };

  const parsedIds = entityIds
    .split(',')
    .map(v => Number.parseInt(v.trim(), 10))
    .filter(Number.isInteger);

  const buildParams = (): EventSourceRefreshParams => {
    if (scope === 'ids') {
      return { entityIds: parsedIds, maxResults };
    }
    return { entityTypes: selectedTypes, maxResults };
  };

  const poll = useCallback(async (id: string, begunAt: number) => {
    try {
      const next = await getWebIqDiscoveryStatus(id);
      setStatus(next);
      if (TERMINAL_STATUSES.includes(next.status)) {
        if (!savedRef.current) {
          savedRef.current = true;
          const record: LastRunRecord<WebIqDiscoveryStatus> = {
            timestamp: new Date().toISOString(),
            triggeredBy: userInfo.userName || userInfo.userId,
            durationMs: Date.now() - begunAt,
            summary: {
              status: next.status,
              entitiesQueued: next.dispatch?.entitiesQueued ?? 0,
              runsCompleted: next.runs?.completed ?? 0,
              sourcesActivated: next.runs?.sourcesActivated ?? 0,
              acceptedSources: next.runs?.acceptedSources ?? 0,
              failed: next.runs?.failed ?? 0,
            },
            response: next,
          };
          saveLastRun(ACTION, record);
          setLastRun(record);
        }
        return;
      }
      pollTimer.current = setTimeout(() => poll(id, begunAt), POLL_INTERVAL_MS);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to read progress';
      setError(message);
    }
  }, [userInfo]);

  useEffect(() => () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
  }, []);

  const handleStart = async () => {
    setConfirmOpen(false);
    setStarting(true);
    setError(null);
    setStatus(null);
    setProcessId(null);
    savedRef.current = false;
    const begunAt = Date.now();
    setStartedAt(begunAt);
    try {
      const started = await startEventSourceRefresh(buildParams());
      setProcessId(started.processId);
      await poll(started.processId, begunAt);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to start the refresh';
      setError(message);
      setStartedAt(null);
    } finally {
      setStarting(false);
    }
  };

  const scopeSummary =
    scope === 'ids'
      ? `${parsedIds.length} specific entit${parsedIds.length === 1 ? 'y' : 'ies'}`
      : `every ${selectedTypes.join(', ') || 'entity'} entity`;

  const canStart =
    !starting &&
    !isRunning &&
    (scope === 'ids' ? parsedIds.length > 0 : selectedTypes.length > 0);

  return (
    <AdminLayout
      currentPage="refreshEventSources"
      pageTitle="Refresh Event Sources with WebIQ"
      pageDescription="Job 1 — find which websites list events for each entity."
    >
      <Card>
        <CardTitle>What this does</CardTitle>
        <CardDescription>
          This searches the web for pages that list events for each entity you select,
          checks that each page is a reusable event listing, scores how well it matches
          the entity, and switches on the ones that pass for review and future extraction.
        </CardDescription>
        <Banner tone="info">
          This job creates <strong>no events by itself</strong> and does not queue extraction
          while <code>WEBIQ_TRIGGER_EXTRACTION</code> is off, which is the safe default.
          Review accepted sources, then start <strong>Extract Events from Web Sources with AI</strong>
          explicitly when the cohort is ready.
        </Banner>
      </Card>

      <Card>
        <CardTitle>Choose what to refresh</CardTitle>
        <CardDescription>
          Start with a single entity type or a few entity IDs. A full refresh of every
          entity takes hours and makes a lot of AI calls.
        </CardDescription>

        <Field>
          <InlineLabel>
            <input
              type="radio"
              checked={scope === 'types'}
              onChange={() => setScope('types')}
            />
            By entity type
          </InlineLabel>
          <InlineLabel>
            <input
              type="radio"
              checked={scope === 'ids'}
              onChange={() => setScope('ids')}
            />
            By specific entity IDs
          </InlineLabel>
        </Field>

        {scope === 'types' && (
          <Field>
            <Label>Entity types</Label>
            {ENTITY_TYPES.map(type => (
              <InlineLabel key={type}>
                <input
                  type="checkbox"
                  checked={selectedTypes.includes(type)}
                  onChange={() => toggleType(type)}
                />
                {displayEntityType(type)}
              </InlineLabel>
            ))}
          </Field>
        )}

        {scope === 'ids' && (
          <Field>
            <Label htmlFor="entityIds">Entity IDs (comma separated)</Label>
            <Input
              id="entityIds"
              value={entityIds}
              placeholder="57604, 56140"
              onChange={e => setEntityIds(e.target.value)}
            />
          </Field>
        )}

        <Field>
          <Label htmlFor="maxResults">Search results per query (1&ndash;50)</Label>
          <Input
            id="maxResults"
            type="number"
            min={1}
            max={50}
            value={maxResults}
            onChange={e => setMaxResults(Number(e.target.value))}
          />
        </Field>

        <ButtonRow>
          <PrimaryButton onClick={() => setConfirmOpen(true)} disabled={!canStart}>
            <Play size={16} /> Start refresh
          </PrimaryButton>
          {processId && (
            <SecondaryButton
              onClick={() => processId && poll(processId, startedAt ?? Date.now())}
              disabled={starting}
            >
              <Globe size={16} /> Check progress now
            </SecondaryButton>
          )}
        </ButtonRow>

        {starting && startedAt != null && (
          <RunningIndicator startedAt={startedAt} message="Starting the refresh…" />
        )}
        {isRunning && startedAt != null && (
          <RunningIndicator
            startedAt={startedAt}
            message="Refresh is running in the background. It keeps going even if you close this page."
          />
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </Card>

      {status && (
        <Card>
          <CardTitle>Progress</CardTitle>
          <CardDescription>
            Process <code>{status.processId}</code> &mdash; status{' '}
            <strong>{status.status.replace(/_/g, ' ')}</strong>
          </CardDescription>
          <StatGrid>
            <Metric label="Entities queued" value={status.dispatch?.entitiesQueued ?? 0} />
            <Metric label="Runs completed" value={status.runs?.completed ?? 0} tone="success" />
            <Metric label="Still searching" value={status.runs?.searching ?? 0} />
            <Metric label="Waiting in queue" value={status.runs?.queued ?? 0} />
            <Metric label="Sources activated" value={status.runs?.sourcesActivated ?? 0} tone="success" />
            <Metric label="Accepted sources" value={status.runs?.acceptedSources ?? 0} tone="success" />
            <Metric label="No results" value={status.runs?.noResults ?? 0} hint="Normal, not an error" />
            <Metric
              label="Failed"
              value={status.runs?.failed ?? 0}
              tone={(status.runs?.failed ?? 0) > 0 ? 'warning' : 'neutral'}
            />
          </StatGrid>

          {status.errors && status.errors.length > 0 && (
            <>
              <CardDescription>First errors reported</CardDescription>
              <SampleTable>
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Code</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {status.errors.slice(0, 10).map((err, i) => (
                    <tr key={`${err.RunId ?? i}`}>
                      <td>{err.EntityId ?? '—'}</td>
                      <td>{err.ErrorCode ?? '—'}</td>
                      <td>{err.ErrorMessage ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            </>
          )}

          <RawJson data={status} label="Full status response" />
        </Card>
      )}

      <LastRunCard record={lastRun} />

      <ConfirmDialog
        open={confirmOpen}
        title="Start Refresh Event Sources with WebIQ"
        body={`This will search the web for ${scopeSummary}. It makes web search and AI calls for every entity, and automatically queues each accepted source for event extraction.`}
        confirmPhrase="REFRESH"
        primaryLabel="Start refresh"
        isProcessing={starting}
        onConfirm={handleStart}
        onCancel={() => setConfirmOpen(false)}
      />
    </AdminLayout>
  );
};

export default AdminRefreshEventSourcesPage;
